import { useState, useCallback } from 'react';
import { PDFViewer } from './components/PDFViewer';
import { FieldsPanel } from './components/FieldsPanel';
import { AgentOutput } from './components/AgentOutput';
import { AdminPortal } from './components/AdminPortal';
import { Dashboard } from './components/Dashboard';
import { useWebSocket } from './hooks/useWebSocket';
import { apiService } from './services/api';
import { InvoiceFields, AgentMessage, FieldData, ItemFieldData, CategoryData, ExceptionData } from './types'
import { Layers, Folder, BarChart3 } from 'lucide-react'
import { LandingPage } from './components/LandingPage'
import Auth from './Auth';

function AppContent() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fields, setFields] = useState<InvoiceFields>({ headers: [], items: [], categorization: undefined, exceptions: undefined });
  const [fieldHistory, setFieldHistory] = useState<Record<string, Array<{ source: string; value: string | number }>>>({});
  const [workflowData, setWorkflowData] = useState<any>(null);
  const [enhancedFeedback, setEnhancedFeedback] = useState<Array<{
    fieldName: string;
    originalValue: string | number;
    modifiedValue: string | number;
    textFeedback: string;
    screenshotUrl?: string;
    isItem?: boolean;
    itemIndex?: number;
  }>>([]);
  const [currentView, setCurrentView] = useState<'landing-page' | 'invoice-processing' | 'dashboard' | 'admin-portal'>('landing-page');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [watchTowerDataAvailable, setWatchTowerDataAvailable] = useState(false);

  const sidebarOptions = [
    {
      key: 'dashboard',
      icon: <BarChart3 className="w-7 h-7" />,
      label: 'Dashboard',
      tooltip: 'Dashboard',
    },
    {
      key: 'invoice-processing',
      icon: <Layers className="w-7 h-7" />,
      label: 'Invoice',
      tooltip: 'Invoice Processing',
    },
    {
      key: 'admin-portal',
      icon: <Folder className="w-7 h-7" />,
      label: 'Admin',
      tooltip: 'Admin Portal',
    },
  ];
  // Sidebar navigation handler
  const handleSidebarNav = (key: string) => {
    setCurrentView(key as typeof currentView);
    setShowMobileMenu(false);
    // Optionally, scroll to section if you have anchors
    // document.getElementById(key)?.scrollIntoView({ behavior: 'smooth' });
  };
  const {
    messages,
    isConnected,
    connect,
    sendMessage,
    disconnect,
    clearMessages,
    restoreMessages
  } = useWebSocket({
    url: apiService.getWebSocketUrl(),
    fileName: selectedFile?.name || "",
    onMessage: handleAgentMessage,
    onError: (error) => {
      console.error('WebSocket error:', error);
      setIsProcessing(false);
    },
    onClose: () => {
      console.log('WebSocket closed');
      setIsProcessing(false);
    }
  });


  // Helper function to generate invoice ID from filename (matches backend logic)
  const generateInvoiceId = async (fileName: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(fileName);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex.substring(0, 12);
  };

  function handleAgentMessage(message: AgentMessage) {
    console.log('Received message:', message);
    console.log('Message role:', message.role, 'Content preview:', message.content.substring(0, 100));

    // Store the message in the database for future restoration 
    // Note: WebSocket hook will add this message to state, so we calculate order based on current length
    if (selectedFile && message.role !== 'user') {
      const messageOrder = messages.length; // Current length will be the order for this new message
      console.log(`[MESSAGE STORAGE] Attempting to store message from ${message.role}`);
      console.log(`[MESSAGE STORAGE] File: ${selectedFile.name}, Order: ${messageOrder}`);
      console.log(`[MESSAGE STORAGE] Content preview: ${message.content.substring(0, 100)}...`);

      apiService.storeInvoiceMessage(selectedFile.name, message.role, message.content, messageOrder)
        .then(response => {
          console.log(`[MESSAGE STORAGE] Response for ${message.role}:`, response);
          if (response.error) {
            console.warn(`[MESSAGE STORAGE] Failed to store message from ${message.role}:`, response.error);
          } else {
            console.log(`[MESSAGE STORAGE] Successfully stored message from ${message.role}`);
          }
        })
        .catch(error => {
          console.warn(`[MESSAGE STORAGE] Error storing message from ${message.role}:`, error);
        });
    } else {
      console.log(`[MESSAGE STORAGE] Skipping message storage - selectedFile: ${!!selectedFile}, role: ${message.role}`);
    }

    if (!selectedFile) {
      console.log('No selected file, skipping message processing');
      return;
    }

    // Check if this is a completion message or exception handling result
    if (message.role === 'Invoice Flow Agent' &&
      message.content.includes('has been completed')) {
      console.log('Invoice flow completed, setting processing to false');
      setIsProcessing(false);
      return;
    }

    // Check if this is a workflow completion message (look for Author Agent as final step)
    if (message.role === 'Author Agent' || message.role === 'AUTHOR_AGENT') {
      console.log('Author Agent completed - workflow finished, setting processing to false and refreshing workflow data');
      setIsProcessing(false);

      // Refresh workflow data after completion
      if (selectedFile) {
        setTimeout(() => {
          fetchWorkflowData(selectedFile.name);
        }, 2000); // Longer delay to ensure database is updated by author agent
      }
      return;
    }

    // Check if this is a revalidation completion message (look for Report Generator as final step)
    if (message.role === 'Report Generator Agent' || message.role === 'REPORT_GENERATOR') {
      console.log('Report Generator completed - revalidation finished, setting processing to false and refreshing data');
      setIsProcessing(false);

      // Refresh invoice data after revalidation completion
      if (selectedFile) {
        setTimeout(async () => {
          const invoiceId = await generateInvoiceId(selectedFile.name);
          await loadCompleteInvoiceData(invoiceId, selectedFile.name);
        }, 2000); // Delay to ensure database is updated by report generator
      }
      return;
    }

    // Also check for individual workflow agent completion (backup detection)
    if ((message.role === 'GR Workflow Trigger Agent' ||
      message.role === 'Non PO Workflow Trigger Agent' ||
      message.role === 'PO Mismatch Workflow Agent') &&
      (message.content.includes('Workflow data prepared') ||
        message.content.includes('workflow already completed') ||
        message.content.includes('Maximum number of reminders') ||
        message.content.includes('No exceptions found'))) {
      console.log('Individual workflow agent completed - but waiting for Author Agent');
      // Don't set processing to false yet - wait for Author Agent
      return;
    }

    // Check if this is an exception result that might trigger workflow skip
    if (message.role === 'EXCEPTION_HANDLER' &&
      message.content.includes('Exception identified')) {
      console.log('Exception found, workflow may skip remaining agents');
    }

    console.log('Setting timeout for API call processing...');
    // Re-enable API calls with delayed execution to avoid WebSocket interference
    setTimeout(async () => {
      console.log('Timeout executed, processing agent:', message.role);
      const invoiceId = await generateInvoiceId(selectedFile.name);
      const sessionId = apiService.getSessionId();
      const projectId = `${invoiceId}-${sessionId}`;
      console.log('Generated projectId:', projectId, 'for file:', selectedFile.name);

      try {
        // Handle each agent type with proper error handling
        switch (message.role) {
          case 'FIELD_EXTRACTOR':
          case 'Field Extractor Agent':
            console.log('Processing Field Extractor data...');
            await fetchFieldExtractorData(invoiceId);
            break;
          case 'FIELD_EXTRACTOR_VISION':
          case 'Field Extractor Vision Agent':
            console.log('Processing Field Extractor Vision data...');
            await fetchFieldExtractorVisionData(invoiceId);
            break;
          case 'FIELD_DERIVER':
          case 'Field Deriver Agent':
            console.log('Processing Field Deriver data...');
            await fetchFieldDeriverData(invoiceId);
            break;
          case 'INVOICE_TYPE_CLASSIFIER':
          case 'Invoice Type Classifier Agent':
            console.log('Processing Invoice Type Classifier data...');
            await fetchInvoiceTypeClassifierData(invoiceId);
            break;
          case 'FIELD_LEVEL_CLASSIFIER':
          case 'Field Level Classifier Agent':
            console.log('Processing Field Level Classifier data...');
            console.log('WebSocket connection status before API call:', isConnected);
            try {
              await fetchFieldLevelClassifierData(invoiceId);
              console.log('Field Level Classifier API call completed successfully');
            } catch (error) {
              console.error('Field Level Classifier API call failed:', error);
            }
            break;
          case 'CRITICAL_FIELDS_VALIDATOR':
          case 'Critical Fields Validator Agent':
            console.log('Processing Critical Fields Validator data...');
            await fetchCriticalFieldsData(invoiceId);
            break;
          case 'FIELD_VALIDATOR':
          case 'Field Validator Agent':
            console.log('Processing Field Validator data...');
            await fetchValidationData(invoiceId);
            break;
          case 'EXCEPTION_HANDLER':
          case 'Exception Handler Agent':
            console.log('Processing Exception Handler data...');
            console.log('WebSocket connection status before Exception Handler API call:', isConnected);
            try {
              await fetchExceptionData(invoiceId);
              console.log('Exception Handler API call completed successfully');
            } catch (error) {
              console.error('Exception Handler API call failed:', error);
            }
            break;
          case 'ANOMALY_DETECTOR':
          case 'Anomaly Detector Agent':
            console.log('Processing Anomaly Detector data...');
            await fetchAnomalyData(invoiceId);
            break;
          case 'WATCH_TOWER':
          case 'Watch Tower Agent':
            console.log('Processing Watch Tower data...');
            console.log('Watch Tower Agent detected! Role:', message.role);
            await fetchWatchTowerData(invoiceId);
            break;
          default:
            console.log(`Agent ${message.role} completed - no specific handler`);
        }
      } catch (error) {
        console.error(`Error processing ${message.role} data:`, error);
      }
    }, 750); // 750ms delay to allow WebSocket message and database writes to complete
  }

  const fetchFieldExtractorVisionData = async (invoiceId: string) => {
    try {
      console.log('Fetching field extractor vision data for invoiceId:', invoiceId);
      const response = await apiService.getFieldExtractorVisionData(invoiceId);
      console.log('Field extractor vision response:', response);
      if (response.data && response.data.fields) {
        const fieldsData = JSON.parse(response.data.fields);

        // Track vision field history
        fieldsData.invoice_attributes?.forEach((attr: any) => {
          setFieldHistory((prev: Record<string, Array<{ source: string; value: string | number }>>) => ({
            ...prev,
            [attr.key]: [...(prev[attr.key] || []), { source: 'Field Extractor Vision Agent', value: attr.value || '' }]
          }));
        });
        fieldsData.invoice_items?.forEach((item: any) => {
          item.item_attributes?.forEach((attr: any) => {
            const historyKey = `${attr.key}_${item.index || 0}`;
            setFieldHistory((prev: Record<string, Array<{ source: string; value: string | number }>>) => ({
              ...prev,
              [historyKey]: [...(prev[historyKey] || []), { source: 'Field Extractor Vision Agent', value: attr.value || '' }]
            }));
          });
        });
      }
    } catch (error) {
      console.error('Error fetching field extractor vision data:', error);
    }
  };

  // Database fetching functions for each agent
  const fetchFieldExtractorData = async (invoiceId: string) => {
    try {
      console.log('Fetching field extractor data for invoiceId:', invoiceId);
      const response = await apiService.getFieldExtractorData(invoiceId);
      console.log('Field extractor response:', response);
      if (response.data && response.data.fields) {
        console.log('Raw fields data:', response.data.fields);
        const fieldsData = JSON.parse(response.data.fields);
        console.log('Parsed fields data:', fieldsData);

        const newHeaders: FieldData[] = fieldsData.invoice_attributes?.map((attr: any) => ({
          key: attr.key,
          value: attr.value || '',
          hasValidation: false,
          isAnimating: true
        })) || [];

        const newItems: ItemFieldData[] = [];
        fieldsData.invoice_items?.forEach((item: any, itemIndex: number) => {
          item.item_attributes?.forEach((attr: any) => {
            newItems.push({
              index: itemIndex,
              key: attr.key,
              value: attr.value || '',
              hasValidation: false,
              isAnimating: true
            });
          });
        });

        // Track field history
        const newHistory: Record<string, Array<{ source: string; value: string | number }>> = {};
        newHeaders.forEach(header => {
          newHistory[header.key] = [{ source: 'Field Extractor Agent', value: header.value }];
        });
        newItems.forEach(item => {
          const historyKey = `${item.key}_${item.index}`;
          newHistory[historyKey] = [{ source: 'Field Extractor Agent', value: item.value }];
        });
        setFieldHistory((prev: Record<string, Array<{ source: string; value: string | number }>>) => ({ ...prev, ...newHistory }));

        console.log('Setting new headers:', newHeaders);
        console.log('Setting new items:', newItems);
        setFields((prev: InvoiceFields) => {
          console.log('Previous fields state:', prev);
          const newState = { ...prev, headers: newHeaders, items: newItems };
          console.log('New fields state:', newState);
          return newState;
        });

        setTimeout(() => {
          setFields((prev: InvoiceFields) => ({
            ...prev,
            headers: prev.headers.map(field => ({ ...field, isAnimating: false })),
            items: prev.items.map(item => ({ ...item, isAnimating: false }))
          }));
        }, 1000);
      } else {
        console.log('No response data or fields found');
        console.log('Full response object:', response);
      }
    } catch (error) {
      console.error('Error fetching field extractor data:', error);
      if (error instanceof Error) {
        console.error('Error details:', error.message, error.stack);
      }
    }
  };

  const fetchCriticalFieldsData = async (invoiceId: string) => {
    try {
      const response = await apiService.getCriticalFieldsData(invoiceId);
      if (response.data && response.data.fields) {
        const fieldsData = JSON.parse(response.data.fields);

        setFields((prev: InvoiceFields) => ({
          ...prev,
          headers: prev.headers.map(field => {
            const updatedField = fieldsData.invoice_attributes?.find((attr: any) => attr.key === field.key);
            return updatedField ? {
              ...field,
              value: updatedField.value || field.value,
              isAnimating: true
            } : field;
          }),
          items: prev.items.map(item => {
            const itemData = fieldsData.invoice_items?.[item.index];
            const updatedField = itemData?.item_attributes?.find((attr: any) => attr.key === item.key);
            return updatedField ? {
              ...item,
              value: updatedField.value || item.value,
              isAnimating: true
            } : item;
          })
        }));

        setTimeout(() => {
          setFields((prev: InvoiceFields) => ({
            ...prev,
            headers: prev.headers.map(field => ({ ...field, isAnimating: false })),
            items: prev.items.map(item => ({ ...item, isAnimating: false }))
          }));
        }, 1000);
      }
    } catch (error) {
      console.error('Error fetching critical fields data:', error);
    }
  };

  const fetchInvoiceTypeClassifierData = async (invoiceId: string) => {
    try {
      const response = await apiService.getInvoiceTypeClassifierData(invoiceId);
      if (response.data) {
        const classification: CategoryData = {
          category: response.data.category,
          invoice_type: response.data.invoice_type,
          invoice_category: response.data.invoice_category,
          reason: response.data.reason
        };

        setFields((prev: InvoiceFields) => ({
          ...prev,
          categorization: classification
        }));
      }
    } catch (error) {
      console.error('Error fetching invoice type classifier data:', error);
    }
  };

  const fetchFieldLevelClassifierData = async (invoiceId: string) => {
    try {
      const response = await apiService.getFieldLevelClassifierData(invoiceId);
      if (response.data && response.data.region !== undefined) {
        console.log('Field level response data:', response.data);
        console.log('Type field value:', response.data?.type);

        setFields(prev => ({
          ...prev,
          categorization: {
            ...prev.categorization,
            region: response.data?.region || '',
            country: response.data?.country || '',
            type: (() => {
              const typeValue = response.data?.type;
              if (!typeValue) return undefined;

              // If it's already an object, return it directly
              if (typeof typeValue === 'object') {
                return typeValue;
              }

              // If it's a string, try to parse it
              if (typeof typeValue === 'string') {
                try {
                  // Try to parse as JSON
                  return JSON.parse(typeValue);
                } catch (error) {
                  console.error('Error parsing type JSON:', error);
                  console.log('Raw type value:', typeValue);

                  // If parsing fails, try to handle it as a simple string
                  try {
                    // Try to parse it as a simple array or object string
                    const cleanedValue = typeValue.trim();
                    if (cleanedValue.startsWith('[') && cleanedValue.endsWith(']')) {
                      return JSON.parse(cleanedValue);
                    }
                    if (cleanedValue.startsWith('{') && cleanedValue.endsWith('}')) {
                      return JSON.parse(cleanedValue);
                    }
                    // If it's just a simple string, return it as is
                    return typeValue;
                  } catch (innerError) {
                    console.error('Error handling type value:', innerError);
                    return typeValue;
                  }
                }
              }

              return typeValue;
            })()
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching field level classifier data:', error);
    }
  };

  const fetchFieldDeriverData = async (invoiceId: string) => {
    try {
      const response = await apiService.getFieldDeriverData(invoiceId);
      if (response.data && response.data.fields) {
        const fieldsData = JSON.parse(response.data.fields);

        setFields((prev: InvoiceFields) => ({
          ...prev,
          headers: prev.headers.map(field => {
            const updatedField = fieldsData.invoice_attributes?.find((attr: any) => attr.key === field.key);
            return updatedField ? {
              ...field,
              value: updatedField.value || field.value,
              isAnimating: true
            } : field;
          }).concat(
            // Add any new derived fields
            fieldsData.invoice_attributes?.filter((attr: any) =>
              !prev.headers.some(h => h.key === attr.key)
            ).map((attr: any) => ({
              key: attr.key,
              value: attr.value || '',
              hasValidation: false,
              isAnimating: true
            })) || []
          )
        }));

        setTimeout(() => {
          setFields((prev: InvoiceFields) => ({
            ...prev,
            headers: prev.headers.map(field => ({ ...field, isAnimating: false }))
          }));
        }, 1000);
      }
    } catch (error) {
      console.error('Error fetching field deriver data:', error);
    }
  };

  const fetchValidationData = async (invoiceId: string) => {
    try {
      const response = await apiService.getValidationData(invoiceId);
      if (response.data) {
        const vmdData = response.data.vmd ? JSON.parse(response.data.vmd) : {};
        const poHeaderData = response.data.po_header ? JSON.parse(response.data.po_header) : {};
        const poLineData = response.data.po_line_i ? JSON.parse(response.data.po_line_i) : [];
        const exceptionsData = response.data.exceptions ? JSON.parse(response.data.exceptions) : [];

        // Create validation status maps
        const validationMap = new Map<string, { status: 'success' | 'error' | 'warning', reason: string, expected?: string }>();

        // Process VMD validation results
        vmdData.valid?.forEach((field: any) => {
          // Check if it's a "No values to compare" case
          const status = field.score === 'No values to compare' ? 'warning' : 'success';
          validationMap.set(field.key, {
            status,
            reason: field.score === 'No values to compare'
              ? 'Warning: No values to compare'
              : `Valid: matches VMD database (score: ${field.score})`,
            expected: field.expected
          });
        });
        vmdData.invalid?.forEach((field: any) => {
          validationMap.set(field.key, {
            status: 'error',
            reason: field.reason || `Invalid: Does not match VMD database (score: ${field.score})`,
            expected: field.expected
          });
        });
        vmdData.not_found?.forEach((field: any) => {
          validationMap.set(field.key, {
            status: 'warning',
            reason: field.reason || 'Warning: Reference data not found in VMD database'
          });
        });

        // Process PO Header validation results
        poHeaderData.valid?.forEach((field: any) => {
          // Check if it's a "No values to compare" case
          const status = field.score === 'No values to compare' ? 'warning' : 'success';
          validationMap.set(field.key, {
            status,
            reason: field.score === 'No values to compare'
              ? 'Warning: No values to compare'
              : `Valid: Matches PO Header database (score: ${field.score})`,
            expected: field.expected
          });
        });
        poHeaderData.invalid?.forEach((field: any) => {
          validationMap.set(field.key, {
            status: 'error',
            reason: field.reason || `Invalid: Does not match PO Header database (score: ${field.score})`,
            expected: field.expected
          });
        });
        poHeaderData.not_found?.forEach((field: any) => {
          validationMap.set(field.key, {
            status: 'warning',
            reason: field.reason || 'Warning: Reference data not found in PO Header database'
          });
        });

        // Process PO Line validation results for items
        poLineData.forEach((itemResult: any, index: number) => {
          itemResult.valid?.forEach((field: any) => {
            // Check if it's a "No values to compare" case
            const status = field.score === 'No values to compare' ? 'warning' : 'success';
            validationMap.set(`${index}-${field.key}`, {
              status,
              reason: field.score === 'No values to compare'
                ? 'Warning: No values to compare'
                : `Valid: Matches PO Line Item database (score: ${field.score})`,
              expected: field.expected
            });
          });
          itemResult.invalid?.forEach((field: any) => {
            validationMap.set(`${index}-${field.key}`, {
              status: 'error',
              reason: field.reason || `Invalid: Does not match PO Line Item database (score: ${field.score})`,
              expected: field.expected
            });
          });
          itemResult.not_found?.forEach((field: any) => {
            validationMap.set(`${index}-${field.key}`, {
              status: 'warning',
              reason: field.reason || 'Warning: Reference data not found in PO Line Item database'
            });
          });
        });

        // Update field history with new validation results (preserving existing history)
        setFieldHistory((prevHistory: Record<string, Array<{ source: string; value: string | number }>>) => {
          const newHistory = { ...prevHistory };

          // Add validation results to field history - iterate through all validation data
          validationMap.forEach((validation, key) => {
            if (validation.expected && validation.expected !== 'None') {
              // For header fields (no index in key)
              if (!key.includes('-')) {
                const historyKey = key;
                if (!newHistory[historyKey]) {
                  newHistory[historyKey] = [];
                }
                // Add validation expected value to history if not already present
                const hasValidationEntry = newHistory[historyKey].some(entry => entry.source === 'Validation Database');
                if (!hasValidationEntry) {
                  newHistory[historyKey].push({
                    source: 'Validation Database',
                    value: validation.expected
                  });
                }
              } else {
                // For item fields (has index in key like "0-Material number")
                const [itemIndex, fieldKey] = key.split('-', 2);
                const historyKey = `${fieldKey}_${itemIndex}`;
                if (!newHistory[historyKey]) {
                  newHistory[historyKey] = [];
                }
                // Add validation expected value to history if not already present
                const hasValidationEntry = newHistory[historyKey].some(entry => entry.source === 'Validation Database');
                if (!hasValidationEntry) {
                  newHistory[historyKey].push({
                    source: 'Validation Database',
                    value: validation.expected
                  });
                }
              }
            }
          });

          return newHistory;
        });

        setFields((prev: InvoiceFields) => ({
          ...prev,
          headers: prev.headers.map(field => {
            const validation = validationMap.get(field.key);
            return validation ? {
              ...field,
              hasValidation: true,
              validationStatus: validation.status,
              validationReason: validation.reason,
              expectedValue: validation.expected,
              isAnimating: true
            } : {
              ...field,
              hasValidation: true,
              validationStatus: field.isAnomaly ? 'error' : 'success' as const,
              validationReason: field.isAnomaly ? field.anomalyReason : 'Field passed validation checks',
              isAnimating: true
            };
          }),
          items: prev.items.map(item => {
            const validation = validationMap.get(`${item.index}-${item.key}`);
            return validation ? {
              ...item,
              hasValidation: true,
              validationStatus: validation.status,
              validationReason: validation.reason,
              expectedValue: validation.expected,
              isAnimating: true
            } : {
              ...item,
              hasValidation: true,
              validationStatus: item.isAnomaly ? 'error' : 'success' as const,
              validationReason: item.isAnomaly ? item.anomalyReason : 'Field passed validation checks',
              isAnimating: true
            };
          })
        }));

        setTimeout(() => {
          setFields((prev: InvoiceFields) => ({
            ...prev,
            headers: prev.headers.map(field => ({ ...field, isAnimating: false })),
            items: prev.items.map(item => ({ ...item, isAnimating: false }))
          }));
        }, 1000);

        // Process Field Validator exceptions if they exist
        if (exceptionsData && exceptionsData.length > 0) {
          const fieldValidatorExceptions = exceptionsData.map((exc: any) => ({
            type: exc.exception,
            items: exc.items,
            source: 'Field Validator Agent'
          }));

          // Update or merge with existing exceptions
          setFields((prev: InvoiceFields) => ({
            ...prev,
            exceptions: {
              ...(prev.exceptions || {}),
              fieldValidatorExceptions
            }
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching validation data:', error);
    }
  };

  const fetchExceptionData = async (invoiceId: string) => {
    try {
      const sessionId = apiService.getSessionId();
      console.log('Fetching exception data for invoiceId:', invoiceId, 'sessionId:', sessionId);
      const response = await apiService.getExceptionsData(invoiceId, sessionId);
      console.log('Exception data response:', response);
      if (response.data && response.data.exception_type) {
        const exceptionData: ExceptionData = {
          exception_type: response.data.exception_type,
          message: getExceptionMessage(response.data.exception_type)
        };

        setFields((prev: InvoiceFields) => ({
          ...prev,
          exceptions: exceptionData
        }));
      } else {
        // No exceptions found
        setFields((prev: InvoiceFields) => ({
          ...prev,
          exceptions: {
            exception_type: 'NO_EXCEPTIONS',
            message: 'No exceptions found, proceeding with validation.'
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching exception data:', error);
    }
  };

  const getExceptionMessage = (exceptionType: string): string => {
    switch (exceptionType) {
      case 'MISSING_GL_AND_CC':
        return 'Missing required fields: GL Account and Cost Center';
      case 'MISSING_GL_ACCOUNT':
        return 'Missing required field: GL Account';
      case 'MISSING_COST_CENTER':
        return 'Missing required field: Cost Center';
      default:
        return `Exception identified: ${exceptionType}`;
    }
  };

  const fetchAnomalyData = async (invoiceId: string) => {
    try {
      console.log('Fetching anomaly data for invoiceId:', invoiceId);
      const response = await apiService.getAnomalyDetectorData(invoiceId);
      console.log('Anomaly data response:', response);
      if (response.data && response.data.anomalies) {
        console.log('Raw anomalies data:', response.data.anomalies);
        const anomaliesData = JSON.parse(response.data.anomalies);
        console.log('Parsed anomalies data:', anomaliesData);
        console.log('Is anomaliesData an array?', Array.isArray(anomaliesData));

        // Handle different anomaly data structures
        let anomaliesArray: any[] = [];
        console.log('Anomaly data type:', typeof anomaliesData);
        console.log('Anomaly data keys:', Object.keys(anomaliesData));

        if (Array.isArray(anomaliesData)) {
          console.log('Case 1: Data is array');
          anomaliesArray = anomaliesData;
        } else if (anomaliesData && typeof anomaliesData === 'object') {
          console.log('Case 2: Data is object');
          // If it's an object, try to extract arrays from its properties
          if (anomaliesData.anomalies && Array.isArray(anomaliesData.anomalies)) {
            console.log('Case 2a: Found anomalies array property');
            anomaliesArray = anomaliesData.anomalies;
          } else if (anomaliesData.fields && Array.isArray(anomaliesData.fields)) {
            console.log('Case 2b: Found fields array property');
            anomaliesArray = anomaliesData.fields;
          } else {
            console.log('Case 2c: Converting object values to array');
            console.log('Object values:', Object.values(anomaliesData));
            // Check if any of the object values are arrays
            const arrayValues = Object.values(anomaliesData).filter(val => Array.isArray(val));
            if (arrayValues.length > 0) {
              console.log('Found array values:', arrayValues);
              anomaliesArray = arrayValues.flat();
            } else {
              // Convert object to array of its values if needed
              anomaliesArray = Object.values(anomaliesData).flat().filter((item: any) =>
                item && typeof item === 'object' && item.field
              );
            }
          }
        }
        console.log('Final anomaliesArray:', anomaliesArray);

        setFields((prev: InvoiceFields) => {
          console.log('Processing anomalies for fields update...');
          console.log('Current headers:', prev.headers.map(h => h.key));
          console.log('Current items:', prev.items.map(i => `${i.key}[${i.index}]`));
          console.log('Anomaly fields:', anomaliesArray.map((a: any) => `${a.field || a.key || 'unknown'}${a.item_index !== undefined ? `[${a.item_index}]` : ''}`));

          const updatedHeaders = prev.headers.map(field => {
            const anomalyField = anomaliesArray.find((anomaly: any) =>
              (anomaly.field === field.key || anomaly.key === field.key)
            );
            if (anomalyField) {
              console.log(`Found anomaly for header field: ${field.key}`, anomalyField);
            }
            return anomalyField ? {
              ...field,
              isAnomaly: true,
              anomalyReason: anomalyField.reason || anomalyField.message || 'Field flagged as anomalous',
              isAnimating: true
            } : field;
          });

          const updatedItems = prev.items.map(item => {
            const anomalyField = anomaliesArray.find((anomaly: any) =>
              (anomaly.field === item.key || anomaly.key === item.key) &&
              (anomaly.item_index === item.index || anomaly.index === item.index)
            );
            if (anomalyField) {
              console.log(`Found anomaly for item field: ${item.key}[${item.index}]`, anomalyField);
            }
            return anomalyField ? {
              ...item,
              isAnomaly: true,
              anomalyReason: anomalyField.reason || anomalyField.message || 'Field flagged as anomalous',
              isAnimating: true
            } : item;
          });

          console.log('Updated headers with anomalies:', updatedHeaders.filter(h => h.isAnomaly));
          console.log('Updated items with anomalies:', updatedItems.filter(i => i.isAnomaly));

          return {
            ...prev,
            headers: updatedHeaders,
            items: updatedItems
          };
        });

        setTimeout(() => {
          setFields((prev: InvoiceFields) => ({
            ...prev,
            headers: prev.headers.map(field => ({ ...field, isAnimating: false })),
            items: prev.items.map(item => ({ ...item, isAnimating: false }))
          }));
        }, 1000);
      }
    } catch (error) {
      console.error('Error fetching anomaly data:', error);
    }
  };

  const fetchWatchTowerData = async (invoiceId: string) => {
    try {
      console.log('Fetching watch tower data for invoiceId:', invoiceId);
      const response = await apiService.getWatchTowerData(invoiceId);
      console.log('Watch tower data response:', response);
      if (response.data) {
        console.log('Watch tower data fetched successfully');
        setWatchTowerDataAvailable(true);
      }
    } catch (error) {
      console.error('Error fetching watch tower data:', error);
    }
  };

  const fetchWorkflowData = async (fileName: string) => {
    try {
      console.log('Fetching workflow data for fileName:', fileName);
      const response = await apiService.getWorkflowData(fileName);
      console.log('Workflow data response:', response);
      console.log('Response data details:', {
        hasData: !!response.data,
        workflowStatus: response.data?.workflow_status,
        reminders: response.data?.reminders,
        error: response.error
      });

      if (response.data) {
        setWorkflowData((prev: any) => response.data);
      } else {
        console.log('No workflow data returned, setting to null');
        setWorkflowData(null);
      }
    } catch (error) {
      console.error('Error fetching workflow data:', error);
      setWorkflowData(null);
    }
  };

  const handleFileSelect = (file: File) => {
    console.log('App received file:', file.name);
    setSelectedFile((prev: File | null) => file);
    setFields((prev: InvoiceFields) => ({ headers: [], items: [], categorization: undefined, exceptions: undefined }));
    setWorkflowData(null);
    setWatchTowerDataAvailable(false);
    clearMessages();

    // Switch to invoice processing view
    setCurrentView('invoice-processing');

    // Fetch workflow data for the selected file
    fetchWorkflowData(file.name);

    console.log('File state updated');
  };

  const handleInvoiceSelect = async (filename: string) => {
    console.log('Invoice selected from dashboard:', filename);

    try {
      // Create a proper file object for PDF viewing - fetch the actual file
      console.log('Fetching PDF file:', filename);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/uploads/${filename}`);
      console.log('PDF fetch response status:', response.status);

      if (response.ok) {
        const blob = await response.blob();
        console.log('PDF blob size:', blob.size);
        const file = new File([blob], filename, { type: 'application/pdf' });
        setSelectedFile(file);
        console.log('PDF file set successfully');
      } else {
        console.log('PDF fetch failed, using fallback');
        // Fallback to fake file if fetch fails
        const file = new File([''], filename, { type: 'application/pdf' });
        setSelectedFile(file);
      }
    } catch (error) {
      console.error('Error fetching PDF file:', error);
      // Fallback to fake file
      const file = new File([''], filename, { type: 'application/pdf' });
      setSelectedFile(file);
    }

    // Clear current state
    setFields({ headers: [], items: [], categorization: undefined, exceptions: undefined });
    setWorkflowData(null);
    setWatchTowerDataAvailable(false);
    clearMessages();

    // Switch to invoice processing view
    setCurrentView('invoice-processing');

    try {
      // Load all the data for this invoice
      const invoiceId = await generateInvoiceId(filename);

      // Load existing fields and all agent data
      await loadCompleteInvoiceData(invoiceId, filename);

      console.log('Invoice data loaded for:', filename);
    } catch (error) {
      console.error('Error loading invoice data:', error);
    }
  };

  const loadCompleteInvoiceData = async (invoiceId: string, filename: string) => {
    console.log('Loading complete invoice data for:', invoiceId, filename);

    try {
      // Load basic fields first
      console.log('Loading basic fields...');
      const fieldsResponse = await apiService.getInvoiceFields(filename);
      console.log('Fields response:', fieldsResponse);

      if (fieldsResponse.data?.fields) {
        const { headers, items } = fieldsResponse.data.fields;

        const processedFields = {
          headers: headers.map(h => ({
            key: h.key,
            value: h.value,
            hasValidation: false,
            validationStatus: undefined as any,
            isAnomaly: false
          })),
          items: items.map(i => ({
            index: i.index,
            key: i.key,
            value: i.value,
            hasValidation: false,
            validationStatus: undefined as any,
            isAnomaly: false
          })),
          categorization: undefined as any,
          exceptions: undefined as any
        };

        console.log('Setting basic fields...');
        setFields(processedFields);

        // Load original agent data into field history
        console.log('Loading agent data for field history...');
        try {
          // Load Field Extractor data for history
          const fieldExtractorResponse = await apiService.getFieldExtractorData(invoiceId);
          if (fieldExtractorResponse.data?.fields) {
            const fieldsData = JSON.parse(fieldExtractorResponse.data.fields);
            setFieldHistory((prevHistory: Record<string, Array<{ source: string; value: string | number }>>) => {
              const newHistory = { ...prevHistory };

              // Add field extractor data to history
              fieldsData.invoice_attributes?.forEach((attr: any) => {
                const historyKey = attr.key;
                if (!newHistory[historyKey]) {
                  newHistory[historyKey] = [];
                }
                // Add only if not already present
                const hasExtractorEntry = newHistory[historyKey].some(entry => entry.source === 'Field Extractor Agent');
                if (!hasExtractorEntry) {
                  newHistory[historyKey].push({
                    source: 'Field Extractor Agent',
                    value: attr.value || ''
                  });
                }
              });

              fieldsData.invoice_items?.forEach((item: any) => {
                item.item_attributes?.forEach((attr: any) => {
                  const historyKey = `${attr.key}_${item.index || 0}`;
                  if (!newHistory[historyKey]) {
                    newHistory[historyKey] = [];
                  }
                  // Add only if not already present
                  const hasExtractorEntry = newHistory[historyKey].some(entry => entry.source === 'Field Extractor Agent');
                  if (!hasExtractorEntry) {
                    newHistory[historyKey].push({
                      source: 'Field Extractor Agent',
                      value: attr.value || ''
                    });
                  }
                });
              });

              return newHistory;
            });
          }
        } catch (error) {
          console.log('No field extractor data found for history:', error);
        }

        try {
          // Load Field Extractor Vision data for history
          const visionResponse = await apiService.getFieldExtractorVisionData(invoiceId);
          if (visionResponse.data?.fields) {
            const fieldsData = JSON.parse(visionResponse.data.fields);
            setFieldHistory((prevHistory: Record<string, Array<{ source: string; value: string | number }>>) => {
              const newHistory = { ...prevHistory };

              // Add vision field history
              fieldsData.invoice_attributes?.forEach((attr: any) => {
                const historyKey = attr.key;
                if (!newHistory[historyKey]) {
                  newHistory[historyKey] = [];
                }
                // Add only if not already present
                const hasVisionEntry = newHistory[historyKey].some(entry => entry.source === 'Field Extractor Vision Agent');
                if (!hasVisionEntry) {
                  newHistory[historyKey].push({
                    source: 'Field Extractor Vision Agent',
                    value: attr.value || ''
                  });
                }
              });

              fieldsData.invoice_items?.forEach((item: any) => {
                item.item_attributes?.forEach((attr: any) => {
                  const historyKey = `${attr.key}_${item.index || 0}`;
                  if (!newHistory[historyKey]) {
                    newHistory[historyKey] = [];
                  }
                  // Add only if not already present
                  const hasVisionEntry = newHistory[historyKey].some(entry => entry.source === 'Field Extractor Vision Agent');
                  if (!hasVisionEntry) {
                    newHistory[historyKey].push({
                      source: 'Field Extractor Vision Agent',
                      value: attr.value || ''
                    });
                  }
                });
              });

              return newHistory;
            });
          }
        } catch (error) {
          console.log('No field extractor vision data found for history:', error);
        }

        // Now try to load additional data one by one
        console.log('Loading validation data...');
        try {
          const validationResponse = await apiService.getValidationData(invoiceId);
          console.log('Validation response:', validationResponse);

          if (validationResponse.data) {
            console.log('Validation data found, applying...');
            const validationData = validationResponse.data;
            const vmdData = validationData.vmd ? JSON.parse(validationData.vmd) : {};
            const poHeaderData = validationData.po_header ? JSON.parse(validationData.po_header) : {};
            const poLineData = validationData.po_line_i ? JSON.parse(validationData.po_line_i) : [];

            // Build field history with validation data (without overwriting existing history)
            setFieldHistory((prevHistory: Record<string, Array<{ source: string; value: string | number }>>) => {
              const newHistory = { ...prevHistory };

              // Add validation expected values to field history for headers
              [...(vmdData.valid || []), ...(vmdData.invalid || []), ...(poHeaderData.valid || []), ...(poHeaderData.invalid || [])].forEach((field: any) => {
                if (field.expected && field.expected !== 'None') {
                  const historyKey = field.key;
                  if (!newHistory[historyKey]) {
                    newHistory[historyKey] = [];
                  }
                  // Add validation expected value to history if not already present
                  const hasValidationEntry = newHistory[historyKey].some(entry => entry.source === 'Validation Database');
                  if (!hasValidationEntry) {
                    newHistory[historyKey].push({
                      source: 'Validation Database',
                      value: field.expected
                    });
                  }
                }
              });

              // Add validation expected values to field history for items
              poLineData.forEach((itemResult: any, index: number) => {
                [...(itemResult.valid || []), ...(itemResult.invalid || [])].forEach((field: any) => {
                  if (field.expected && field.expected !== 'None') {
                    const historyKey = `${field.key}_${index}`;
                    if (!newHistory[historyKey]) {
                      newHistory[historyKey] = [];
                    }
                    // Add validation expected value to history if not already present
                    const hasValidationEntry = newHistory[historyKey].some(entry => entry.source === 'Validation Database');
                    if (!hasValidationEntry) {
                      newHistory[historyKey].push({
                        source: 'Validation Database',
                        value: field.expected
                      });
                    }
                  }
                });
              });

              return newHistory;
            });

            // Apply validation status to fields
            setFields(prev => ({
              ...prev,
              headers: prev.headers.map(field => {
                // Check VMD validation
                let validation = vmdData.valid?.find((v: any) => v.key === field.key) ||
                  vmdData.invalid?.find((v: any) => v.key === field.key) ||
                  vmdData.not_found?.find((v: any) => v.key === field.key);

                // Check PO Header validation if not found in VMD
                if (!validation) {
                  validation = poHeaderData.valid?.find((v: any) => v.key === field.key) ||
                    poHeaderData.invalid?.find((v: any) => v.key === field.key) ||
                    poHeaderData.not_found?.find((v: any) => v.key === field.key);
                }

                if (validation) {
                  // Determine validation status
                  const isValid = vmdData.valid?.find((v: any) => v.key === field.key) || poHeaderData.valid?.find((v: any) => v.key === field.key);
                  const isInvalid = vmdData.invalid?.find((v: any) => v.key === field.key) || poHeaderData.invalid?.find((v: any) => v.key === field.key);

                  // Create meaningful validation message
                  let validationMessage = '';
                  if (validation.reason) {
                    validationMessage = validation.reason;
                  } else if (validation.score === 'No values to compare') {
                    validationMessage = 'Warning: No values to compare';
                  } else if (isValid) {
                    validationMessage = `Valid: matches database (score: ${validation.score})`;
                  } else if (isInvalid) {
                    validationMessage = `Invalid: does not match database (score: ${validation.score})`;
                  } else {
                    validationMessage = 'Warning: reference data not found in database';
                  }

                  return {
                    ...field,
                    hasValidation: true,
                    validationStatus: isValid ? 'success' as const : isInvalid ? 'error' as const : 'warning' as const,
                    validationReason: validationMessage,
                    expectedValue: validation.expected
                  };
                }
                return field;
              }),
              items: prev.items.map(item => {
                const itemValidation = poLineData[item.index];
                if (itemValidation) {
                  const validation = itemValidation.valid?.find((v: any) => v.key === item.key) ||
                    itemValidation.invalid?.find((v: any) => v.key === item.key) ||
                    itemValidation.not_found?.find((v: any) => v.key === item.key);

                  if (validation) {
                    // Determine validation status for items
                    const isValid = itemValidation.valid?.find((v: any) => v.key === item.key);
                    const isInvalid = itemValidation.invalid?.find((v: any) => v.key === item.key);

                    // Create meaningful validation message for items
                    let validationMessage = '';
                    if (validation.reason) {
                      validationMessage = validation.reason;
                    } else if (validation.score === 'No values to compare') {
                      validationMessage = 'Warning: No values to compare';
                    } else if (isValid) {
                      validationMessage = `Valid: Matches PO Line Item database (score: ${validation.score})`;
                    } else if (isInvalid) {
                      validationMessage = `Invalid: Does not match PO Line Item database (score: ${validation.score})`;
                    } else {
                      validationMessage = 'Warning: Reference data not found in PO Line Item database';
                    }

                    return {
                      ...item,
                      hasValidation: true,
                      validationStatus: isValid ? 'success' as const : isInvalid ? 'error' as const : 'warning' as const,
                      validationReason: validationMessage,
                      expectedValue: validation.expected
                    };
                  }
                }
                return item;
              })
            }));
          }
        } catch (error) {
          console.log('No validation data or error:', error);
        }

        // Load anomaly data
        console.log('Loading anomaly data...');
        try {
          const anomalyResponse = await apiService.getAnomalyDetectorData(invoiceId);
          console.log('Anomaly response:', anomalyResponse);

          if (anomalyResponse.data?.anomalies) {
            const anomaliesData = JSON.parse(anomalyResponse.data.anomalies);
            console.log('Anomaly data found, applying...');

            // Apply anomalies to fields
            setFields(prev => ({
              ...prev,
              headers: prev.headers.map(field => {
                const anomaly = anomaliesData.invoice_attributes?.find((a: any) => a.key === field.key);
                if (anomaly) {
                  return {
                    ...field,
                    isAnomaly: true,
                    anomalyReason: anomaly.reason
                  };
                }
                return field;
              }),
              items: prev.items.map(item => {
                const itemAnomalies = anomaliesData.invoice_items?.[item.index];
                if (itemAnomalies) {
                  const anomaly = itemAnomalies.find((a: any) => a.key === item.key);
                  if (anomaly) {
                    return {
                      ...item,
                      isAnomaly: true,
                      anomalyReason: anomaly.reason
                    };
                  }
                }
                return item;
              })
            }));
          }
        } catch (error) {
          console.log('No anomaly data or error:', error);
        }

        console.log('Loading categorization data...');
        try {
          const classifierResponse = await apiService.getInvoiceTypeClassifierData(invoiceId);
          console.log('Classifier response:', classifierResponse);
          if (classifierResponse.data) {
            setFields(prev => ({
              ...prev,
              categorization: {
                category: classifierResponse.data?.category || '',
                invoice_type: Array.isArray(classifierResponse.data?.invoice_type)
                  ? classifierResponse.data?.invoice_type || []
                  : [classifierResponse.data?.invoice_type || ''],
                invoice_category: Array.isArray(classifierResponse.data?.invoice_category)
                  ? classifierResponse.data?.invoice_category || []
                  : [classifierResponse.data?.invoice_category || ''],
                reason: classifierResponse.data?.reason || ''
              }
            }));
          }
        } catch (error) {
          console.log('No classifier data or error:', error);
        }

        console.log('Loading field level classification...');
        try {
          const fieldLevelResponse = await apiService.getFieldLevelClassifierData(invoiceId);
          console.log('Field level response:', fieldLevelResponse);
          if (fieldLevelResponse.data) {
            console.log('Field level response data:', fieldLevelResponse.data);
            console.log('Type field value:', fieldLevelResponse.data?.type);

            setFields(prev => ({
              ...prev,
              categorization: {
                ...prev.categorization,
                region: fieldLevelResponse.data?.region || '',
                country: fieldLevelResponse.data?.country || '',
                type: (() => {
                  const typeValue = fieldLevelResponse.data?.type;
                  if (!typeValue) return undefined;

                  // If it's already an object, return it directly
                  if (typeof typeValue === 'object') {
                    return typeValue;
                  }

                  // If it's a string, try to parse it
                  if (typeof typeValue === 'string') {
                    try {
                      // Handle special case like {PO} - convert to ["PO"]
                      const cleanedValue = typeValue.trim();

                      // If it looks like {PO}, convert it to ["PO"]
                      if (cleanedValue.startsWith('{') && cleanedValue.endsWith('}') && !cleanedValue.includes(':')) {
                        const content = cleanedValue.slice(1, -1).trim();
                        return [content];
                      }

                      // Try to parse as JSON first
                      return JSON.parse(cleanedValue);
                    } catch (error) {
                      console.log('JSON parse failed, trying alternative parsing for:', typeValue);

                      // If parsing fails, try to handle it as a simple string
                      try {
                        const cleanedValue = typeValue.trim();

                        // Handle {PO} format - extract content and make it an array
                        if (cleanedValue.startsWith('{') && cleanedValue.endsWith('}')) {
                          const content = cleanedValue.slice(1, -1).trim();
                          return content ? [content] : [];
                        }

                        // Handle [PO] format
                        if (cleanedValue.startsWith('[') && cleanedValue.endsWith(']')) {
                          const content = cleanedValue.slice(1, -1).trim();
                          return content ? [content] : [];
                        }

                        // If it's just a simple string, return it as an array
                        return [typeValue];
                      } catch (innerError) {
                        console.error('Error handling type value:', innerError);
                        return [typeValue];
                      }
                    }
                  }

                  return typeValue;
                })()
              }
            }));
          }
        } catch (error) {
          console.log('No field level data or error:', error);
        }

        console.log('Loading exception data...');
        try {
          const exceptionResponse = await apiService.getExceptionsData(invoiceId, apiService.getSessionId());
          console.log('Exception response:', exceptionResponse);
          console.log('Exception response data:', exceptionResponse.data);

          // Also try to get field validator exceptions from validation data
          let fieldValidatorExceptions: Array<{
            type: string;
            items: any[];
            source: string;
          }> | undefined = undefined;
          try {
            const validationResponse = await apiService.getValidationData(invoiceId);
            if (validationResponse.data?.exceptions) {
              const exceptionsData = JSON.parse(validationResponse.data.exceptions);
              if (exceptionsData && exceptionsData.length > 0) {
                fieldValidatorExceptions = exceptionsData.map((exc: any) => ({
                  type: exc.exception,
                  items: exc.items,
                  source: 'Field Validator Agent'
                }));
                console.log('Field validator exceptions found:', fieldValidatorExceptions);
              }
            }
          } catch (error) {
            console.log('No field validator exceptions found:', error);
          }

          if (exceptionResponse.data?.exception_type) {
            console.log('Setting exception data:', exceptionResponse.data.exception_type);
            setFields(prev => ({
              ...prev,
              exceptions: {
                exception_type: exceptionResponse.data?.exception_type || '',
                message: getExceptionMessage(exceptionResponse.data?.exception_type || ''),
                fieldValidatorExceptions
              }
            }));
          } else {
            console.log('No exception_type found, setting NO_EXCEPTIONS');
            // Set a default "no exceptions" state to show the card
            setFields(prev => ({
              ...prev,
              exceptions: {
                exception_type: 'NO_EXCEPTIONS',
                message: 'No exceptions found, proceeding with validation.',
                fieldValidatorExceptions
              }
            }));
          }
        } catch (error) {
          console.log('No exception data or error:', error);
          // Set a default "no exceptions" state to show the card even on error
          setFields(prev => ({
            ...prev,
            exceptions: {
              exception_type: 'NO_EXCEPTIONS',
              message: 'No exceptions found, proceeding with validation.'
            }
          }));
        }
      } else {
        console.log('No fields data found');
      }

      // Load workflow data
      console.log('Loading workflow data...');
      await fetchWorkflowData(filename);

      // Restore agent messages
      console.log('[MESSAGE RETRIEVAL] Loading agent messages for:', filename);
      try {
        const messagesResponse = await apiService.getInvoiceMessages(filename);
        console.log('[MESSAGE RETRIEVAL] Full response:', messagesResponse);
        console.log('[MESSAGE RETRIEVAL] Messages data:', messagesResponse.data);

        if (messagesResponse.data?.messages && messagesResponse.data.messages.length > 0) {
          console.log('[MESSAGE RETRIEVAL] Raw messages from API:', messagesResponse.data.messages);
          console.log('[MESSAGE RETRIEVAL] Number of messages found:', messagesResponse.data.messages.length);

          // Log each message for debugging
          messagesResponse.data.messages.forEach((msg, index) => {
            console.log(`[MESSAGE RETRIEVAL] Message ${index + 1}: Role=${msg.role}, Order=${msg.order}, Content=${msg.content.substring(0, 100)}...`);
          });

          const restoredMessages = messagesResponse.data.messages.map(msg => ({
            role: msg.role,
            content: msg.content
          }));
          console.log('[MESSAGE RETRIEVAL] Processed messages for restore:', restoredMessages);
          restoreMessages(restoredMessages);
          console.log('[MESSAGE RETRIEVAL] Restored stored messages:', restoredMessages.length);
        } else {
          console.log('[MESSAGE RETRIEVAL] No messages array found in response or empty array');
          console.log('[MESSAGE RETRIEVAL] Response data structure:', messagesResponse.data);
        }
      } catch (error) {
        console.log('No messages found or error:', error);
        console.error('Full error details:', error);
      }

      // Check for watch tower data
      console.log('Loading watch tower data...');
      try {
        const watchTowerResponse = await apiService.getWatchTowerData(invoiceId);
        if (watchTowerResponse.data) {
          setWatchTowerDataAvailable(true);
        }
      } catch (error) {
        console.log('No watch tower data found or error:', error);
      }

    } catch (error) {
      console.error('Error loading complete invoice data:', error);
    }
  };

  const handleFieldUpdate = (fieldKey: string, value: string | number, isItem?: boolean, itemIndex?: number) => {
    setFields((prev: InvoiceFields) => {
      if (isItem && itemIndex !== undefined) {
        const updatedItems = prev.items.map((item: ItemFieldData) => {
          if (item.key === fieldKey && item.index === itemIndex) {
            return { ...item, value };
          }
          return item;
        });
        return { ...prev, items: updatedItems };
      } else {
        const updatedHeaders = prev.headers.map((header: FieldData) => {
          if (header.key === fieldKey) {
            return { ...header, value };
          }
          return header;
        });
        return { ...prev, headers: updatedHeaders };
      }
    });
  };

  const handleEnhancedFeedback = (feedbackData: {
    fieldName: string;
    originalValue: string | number;
    modifiedValue: string | number;
    textFeedback: string;
    screenshotUrl?: string;
    isItem?: boolean;
    itemIndex?: number;
  }) => {
    setEnhancedFeedback(prev => {
      // Remove any existing feedback for this field
      const filtered = prev.filter(item => {
        if (feedbackData.isItem) {
          return !(item.fieldName === feedbackData.fieldName &&
            item.isItem === feedbackData.isItem &&
            item.itemIndex === feedbackData.itemIndex);
        } else {
          return !(item.fieldName === feedbackData.fieldName && !item.isItem);
        }
      });

      // Add the new feedback
      return [...filtered, feedbackData];
    });
  };

  const handleAddItem = () => {
    const newItemIndex = fields.items.length > 0
      ? Math.max(...fields.items.map((item: ItemFieldData) => item.index)) + 1
      : 0;

    // Get unique keys from existing items
    const existingKeys = Array.from(new Set(fields.items.map((item: ItemFieldData) => item.key)));
    const defaultKeys: string[] = existingKeys.length > 0
      ? existingKeys as string[]
      : ['Material number', 'Quantity', 'Unit price', 'Item amount'];

    const newItems: ItemFieldData[] = defaultKeys.map((key: string) => ({
      index: newItemIndex,
      key,
      value: '',
      hasValidation: false,
      isAnimating: false
    }));

    setFields((prev: InvoiceFields) => ({
      ...prev,
      items: [...prev.items, ...newItems]
    }));
  };

  const handleConfirmChanges = async () => {
    if (!selectedFile) return;

    try {
      // Check if we have enhanced feedback data to submit
      if (enhancedFeedback.length > 0) {
        // Submit enhanced feedback
        const enhancedFeedbackData = {
          enhancedFeedback: enhancedFeedback
        };

        const response = await apiService.submitFeedback(selectedFile.name, enhancedFeedbackData);
        if (response.error) {
          alert('Error submitting enhanced feedback: ' + response.error);
        } else {
          alert('Enhanced feedback submitted successfully!');
          // Clear the enhanced feedback after successful submission
          setEnhancedFeedback([]);
        }
      } else {
        // Fall back to legacy feedback format
        const feedbackData = {
          feedback: {
            headers: fields.headers.map(field => ({
              key: field.key,
              feedback: String(field.value)
            })),
            items: fields.items.map(item => ({
              index: item.index,
              key: item.key,
              feedback: String(item.value)
            }))
          }
        };

        const response = await apiService.submitFeedback(selectedFile.name, feedbackData);
        if (response.error) {
          alert('Error submitting feedback: ' + response.error);
        } else {
          alert('Changes confirmed and posted successfully!');
        }
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Error submitting feedback');
    }
  };

  const handleRevalidate = async () => {
    if (!selectedFile) return;

    try {
      // First, update the critical_fields table with current UI data
      const fieldsData = {
        headers: fields.headers.map(field => ({
          key: field.key,
          value: field.value
        })),
        items: fields.items.map(item => ({
          index: item.index,
          key: item.key,
          value: item.value
        }))
      };

      console.log('Updating critical_fields for revalidation:', selectedFile.name);
      console.log('Fields data:', fieldsData);

      // Call the API to update critical_fields table
      const updateResponse = await apiService.updateCriticalFields(selectedFile.name, fieldsData);

      if (updateResponse.error) {
        alert('Error updating critical fields: ' + updateResponse.error);
        console.error('Critical fields update error:', updateResponse.error);
        return;
      }

      console.log('Critical fields updated successfully, starting revalidation...');

      // Set processing state to show the processing animation
      setIsProcessing(true);

      // Connect to WebSocket if not already connected
      if (!isConnected) {
        await connect();
        // Wait for connection to be established
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Send the revalidation message via WebSocket for real-time streaming
      // const message = {
      //   query: 'Please rerun the validations.',
      //   user_id: 'revalidation-user',
      //   file_name: selectedFile.name
      // };

      const message = {
        "content": {
          "text": "Call the 'Invoice Revalidation Agent' and please rerun the validations.",
        }
      }

      console.log('Sending revalidation message via WebSocket:', message);
      sendMessage(message);

    } catch (error) {
      console.error('Error during revalidation:', error);
      setIsProcessing(false);
      alert('Error during revalidation');
    }
  };

  const handleTriggerWorkflow = async (workflowType: string) => {
    if (!selectedFile) return;

    try {
      // Determine the appropriate query based on workflow type
      let query = '';
      switch (workflowType) {
        case 'PO_LINE_ITEM_MISMATCH':
          query = 'Please trigger PO Mismatch workflow.';
          break;
        case 'GR_NOT_AVAILABLE':
          query = 'Please trigger GR workflow.';
          break;
        case 'TAX_CODE_MISMATCH':
        case 'MISSING_GL_AND_CC':
        case 'MISSING_GL_ACCOUNT':
        case 'MISSING_COST_CENTER':
          query = 'Please trigger Non-PO workflow.';
          break;
        default:
          query = `Please trigger workflow for exception type ${workflowType}.`;
      }

      console.log('Triggering workflow:', workflowType, 'with query:', query);

      // Set processing state to show the processing animation
      setIsProcessing(true);

      // Connect to WebSocket if not already connected
      if (!isConnected) {
        await connect();
        // Wait for connection to be established
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Send the workflow trigger message via WebSocket for real-time streaming
      // const message = {
      //   query: query,
      //   user_id: 'user-123',
      //   file_name: selectedFile.name
      // };

      const message = {
        "content": {
          "text": query,
        }
      }

      console.log('Sending workflow trigger message via WebSocket:', message);
      sendMessage(message);

    } catch (error) {
      console.error('Error triggering workflow:', error);
      setIsProcessing(false);
      alert('Error triggering workflow');
    }
  };

  const handleProcessStart = useCallback(async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setFields((prev: InvoiceFields) => ({ headers: [], items: [], categorization: undefined, exceptions: undefined }));
    clearMessages();

    try {
      // First upload the file to the server
      const uploadResponse = await apiService.uploadFile(selectedFile);
      if (uploadResponse.error) {
        console.error('Error uploading file:', uploadResponse.error);
        alert('Error uploading file: ' + uploadResponse.error);
        setIsProcessing(false);
        return;
      }

      console.log('File uploaded successfully:', uploadResponse.data);

      // Then connect to WebSocket and start processing
      await connect();

      // Wait a bit for the connection to be fully established
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Send the processing request via WebSocket
      // const message = {
      //   query: `Please help run invoice analysis for the invoice ${selectedFile.name}`,
      //   user_id: 'user-123', // You might want to make this configurable
      //   file_name: selectedFile.name
      // };

      const message = {
        "content": {
          "text": `Please help run invoice analysis for the invoice ${selectedFile.name}`,
        }
      }

      console.log('About to send message:', message);
      sendMessage(message);
    } catch (error) {
      console.error('Error starting processing:', error);
      setIsProcessing(false);
    }
  }, [selectedFile, connect, sendMessage, clearMessages]);

  const handleRemoveItem = (itemIndex: number) => {
    setFields((prev: InvoiceFields) => ({
      ...prev,
      items: prev.items.filter((item: ItemFieldData) => item.index !== itemIndex)
    }));
  };

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex flex-row relative transition-colors duration-200">
      {/* Sidebar - interactive */}
      {currentView !== 'landing-page' &&(
      <aside className="w-20 bg-blue-600 flex flex-col items-center py-6 space-y-8 min-h-screen shadow-lg">
        {sidebarOptions.map(opt => (
          <button
            key={opt.key}
            onClick={() => handleSidebarNav(opt.key)}
            className={`flex flex-col items-center justify-center w-fit h-fit px-2 py-2 rounded-lg mb-2 transition-all duration-200
              ${currentView === opt.key
                ? 'bg-white text-blue-600 shadow font-bold scale-110'
                : 'text-blue-100 hover:bg-blue-500 hover:text-white'
              }`}
            title={opt.tooltip}
            aria-label={opt.tooltip}
          >
            {opt.icon}
            <span className="text-[10px] mt-1 font-medium">{opt.label}</span>
          </button>
        ))}
      </aside>
      )}

      {/* Main Content - fills the rest */}
      <div className="flex-1 flex flex-col relative min-w-0">
        {currentView === 'invoice-processing' && (
          <div className="text-2xl font-bold text-gray-800 mt-0 mb-0 p-6">
            Invoice Details
          </div>
        )}

        {/* Overlay for mobile menu */}
        {showMobileMenu && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-40 transition-opacity duration-200"
            onClick={() => setShowMobileMenu(false)}
          />
        )}

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          {currentView === 'invoice-processing' ? (
            /* Invoice Processing System */
            <Auth>
            <div className="flex flex-1 min-w-0 gap-6 h-full p-6 bg-[#f7fafd] transition-colors duration-200">
              {/* PDF Viewer Card */}
              <div className="w-[30%] min-w-0 h-full bg-white rounded-xl shadow-md p-6 flex flex-col">
                <PDFViewer
                  file={selectedFile}
                  isCollapsed={false}
                  onFileSelect={handleFileSelect}
                  onProcessStart={handleProcessStart}
                  isProcessing={isProcessing}
                />
              </div>
              {/* Fields Panel Card */}
              <div className="w-[40%] min-w-0 h-full bg-white rounded-xl shadow-md p-6 flex flex-col">
                <FieldsPanel
                  fields={fields}
                  isCollapsed={false}
                  isProcessing={isProcessing}
                  onFieldUpdate={handleFieldUpdate}
                  onAddItem={handleAddItem}
                  onRemoveItem={handleRemoveItem}
                  onConfirmChanges={handleConfirmChanges}
                  onRevalidate={handleRevalidate}
                  fieldHistory={fieldHistory}
                  onTriggerWorkflow={handleTriggerWorkflow}
                  workflowData={workflowData}
                  selectedFile={selectedFile}
                  onEnhancedFeedback={handleEnhancedFeedback}
                  watchTowerDataAvailable={watchTowerDataAvailable}
                />
              </div>
              {/* Agent Output Card */}
              <div className="w-[30%] min-w-0 h-full bg-white rounded-xl shadow-md p-6 flex flex-col">
                <AgentOutput
                  messages={messages}
                  isCollapsed={false}
                  isProcessing={isProcessing}
                />
              </div>
            </div>
            </Auth>
        
          ) : currentView === 'landing-page' ? (
            <div className="flex-1 h-full overflow-hidden bg-white dark:bg-gray-900 transition-colors duration-200">
               <Auth>
              <LandingPage onNext={() => setCurrentView('dashboard')}/>
                </Auth>
            </div>
          ) :currentView === 'dashboard' ?(
              <div className="flex-1 h-full overflow-auto bg-white dark:bg-gray-900 transition-colors duration-200">
                <Auth>
                <Dashboard onInvoiceSelect={handleInvoiceSelect} onAddInvoice={handleFileSelect} />
                </Auth>
              </div>
          ) :(
            <div className="flex-1 h-full overflow-auto bg-white dark:bg-gray-900 transition-colors duration-200">
              <Auth>
              <AdminPortal />
              </Auth>
            </div>
          )}
        </div>
        <footer className="bg-white py-2">
          <div className="flex items-center max-w-5xl">
            {/* <img src="" alt="Logo" className="w-16 h-auto mr-4" /> */}
            <div className="flex gap-8 text-sm text-black-700 px-4">
              <span className="font-normal">Terms of Use</span>
              <span className="font-normal">Privacy Statement</span>
              <span className="font-normal">Contact Support</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

function App() {

  return (
    <AppContent />
  );
}

export default App;